import { fetchAuthSession } from 'aws-amplify/auth';
import type { CompletedPart, S3Client as S3ClientTyp } from '@aws-sdk/client-s3';
import { backendAusgaben } from '@/lib/amplify';

/**
 * Direkter S3-Zugriff für fortsetzbare Uploads (PHASENPLAN 1.6).
 *
 * Die High-Level-API von Amplify Storage kann einen abgebrochenen
 * Multipart-Upload nicht über einen App-Neustart hinweg fortsetzen. Genau das
 * braucht ein Gerät, das im Keller das Netz verliert — deshalb hier der
 * direkte Weg mit selbst verwaltetem `uploadId` und Teileliste.
 *
 * Der S3-Client wird verzögert geladen; er ist groß und wird nur beim Sync
 * gebraucht.
 */

/** 5 MB ist die kleinste von S3 erlaubte Teilgröße (außer beim letzten Teil). */
export const TEIL_GROESSE_BYTES = 5 * 1024 * 1024;

/** Unterhalb dieser Grenze lohnt kein Multipart-Overhead. */
export const MULTIPART_AB_BYTES = 8 * 1024 * 1024;

let clientVersprechen: Promise<S3ClientTyp> | null = null;

const holeClient = async (): Promise<S3ClientTyp> => {
  if (!clientVersprechen) {
    clientVersprechen = (async () => {
      const { S3Client } = await import('@aws-sdk/client-s3');
      const ausgaben = backendAusgaben();
      const region = (ausgaben?.custom?.region as string | undefined) ?? 'eu-central-1';
      return new S3Client({
        region,
        // Bei jedem Aufruf frische Anmeldedaten — die Sitzung läuft 12 Stunden,
        // ein Upload kann sie überdauern.
        credentials: async () => {
          const session = await fetchAuthSession();
          const anmeldedaten = session.credentials;
          if (!anmeldedaten) throw new Error('Keine gültige Sitzung für den Upload.');
          return {
            accessKeyId: anmeldedaten.accessKeyId,
            secretAccessKey: anmeldedaten.secretAccessKey,
            ...(anmeldedaten.sessionToken ? { sessionToken: anmeldedaten.sessionToken } : {}),
            ...(anmeldedaten.expiration ? { expiration: anmeldedaten.expiration } : {}),
          };
        },
      });
    })();
  }
  return clientVersprechen;
};

export const bucketName = (): string => {
  const ausgaben = backendAusgaben();
  const name = ausgaben?.custom?.dateiBucket as string | undefined;
  if (!name) throw new Error('Der Dateispeicher ist nicht konfiguriert.');
  return name;
};

export interface UploadZustand {
  uploadId: string | null;
  teile: CompletedPart[];
}

export interface UploadOptionen {
  key: string;
  daten: Blob;
  mimeType: string;
  /** Zustand eines abgebrochenen Uploads, um dort weiterzumachen. */
  zustand?: UploadZustand | null;
  /** Wird nach jedem fertigen Teil aufgerufen, damit der Stand überlebt. */
  aufZustand?: (zustand: UploadZustand) => Promise<void> | void;
  aufFortschritt?: (anteil: number) => void;
  abbruch?: AbortSignal;
}

/** Einfacher Upload für kleine Dateien — ein Aufruf, kein Zustand nötig. */
const einteilig = async (optionen: UploadOptionen): Promise<string> => {
  const { PutObjectCommand } = await import('@aws-sdk/client-s3');
  const client = await holeClient();

  await client.send(
    new PutObjectCommand({
      Bucket: bucketName(),
      Key: optionen.key,
      Body: new Uint8Array(await optionen.daten.arrayBuffer()),
      ContentType: optionen.mimeType,
      ServerSideEncryption: 'aws:kms',
    }),
    { abortSignal: optionen.abbruch },
  );

  optionen.aufFortschritt?.(1);
  return optionen.key;
};

/**
 * Mehrteiliger, fortsetzbarer Upload.
 *
 * Nach jedem fertigen Teil wird der Zustand gesichert. Bricht die App ab,
 * beginnt der nächste Versuch bei genau dem Teil, der fehlte — nicht von vorn.
 */
const mehrteilig = async (optionen: UploadOptionen): Promise<string> => {
  const { CompleteMultipartUploadCommand, CreateMultipartUploadCommand, UploadPartCommand } =
    await import('@aws-sdk/client-s3');
  const client = await holeClient();
  const bucket = bucketName();

  let uploadId = optionen.zustand?.uploadId ?? null;
  const fertigeTeile: CompletedPart[] = [...(optionen.zustand?.teile ?? [])];

  if (!uploadId) {
    const erzeugt = await client.send(
      new CreateMultipartUploadCommand({
        Bucket: bucket,
        Key: optionen.key,
        ContentType: optionen.mimeType,
        ServerSideEncryption: 'aws:kms',
      }),
      { abortSignal: optionen.abbruch },
    );
    uploadId = erzeugt.UploadId ?? null;
    if (!uploadId) throw new Error('S3 hat keine Upload-Kennung geliefert.');
    await optionen.aufZustand?.({ uploadId, teile: [] });
  }

  const gesamtTeile = Math.ceil(optionen.daten.size / TEIL_GROESSE_BYTES);
  const erledigt = new Set(fertigeTeile.map((teil) => teil.PartNumber));

  for (let nummer = 1; nummer <= gesamtTeile; nummer += 1) {
    if (erledigt.has(nummer)) continue;
    if (optionen.abbruch?.aborted) throw new Error('Upload abgebrochen.');

    const von = (nummer - 1) * TEIL_GROESSE_BYTES;
    const bis = Math.min(von + TEIL_GROESSE_BYTES, optionen.daten.size);
    const stueck = optionen.daten.slice(von, bis);

    const antwort = await client.send(
      new UploadPartCommand({
        Bucket: bucket,
        Key: optionen.key,
        UploadId: uploadId,
        PartNumber: nummer,
        Body: new Uint8Array(await stueck.arrayBuffer()),
      }),
      { abortSignal: optionen.abbruch },
    );

    fertigeTeile.push({ PartNumber: nummer, ETag: antwort.ETag });
    await optionen.aufZustand?.({ uploadId, teile: [...fertigeTeile] });
    optionen.aufFortschritt?.(fertigeTeile.length / gesamtTeile);
  }

  fertigeTeile.sort((a, b) => (a.PartNumber ?? 0) - (b.PartNumber ?? 0));

  await client.send(
    new CompleteMultipartUploadCommand({
      Bucket: bucket,
      Key: optionen.key,
      UploadId: uploadId,
      MultipartUpload: { Parts: fertigeTeile },
    }),
    { abortSignal: optionen.abbruch },
  );

  return optionen.key;
};

export const hochladen = async (optionen: UploadOptionen): Promise<string> =>
  optionen.daten.size >= MULTIPART_AB_BYTES ? mehrteilig(optionen) : einteilig(optionen);

/** Bricht einen angefangenen Multipart-Upload ab und gibt die Teile frei. */
export const uploadAbbrechen = async (key: string, uploadId: string): Promise<void> => {
  const { AbortMultipartUploadCommand } = await import('@aws-sdk/client-s3');
  const client = await holeClient();
  await client
    .send(new AbortMultipartUploadCommand({ Bucket: bucketName(), Key: key, UploadId: uploadId }))
    .catch(() => undefined);
};

/** Lädt eine Datei herunter — für die Dokumentvorschau in der Objektakte. */
export const herunterladen = async (key: string): Promise<Blob> => {
  const { GetObjectCommand } = await import('@aws-sdk/client-s3');
  const client = await holeClient();
  const antwort = await client.send(new GetObjectCommand({ Bucket: bucketName(), Key: key }));
  const bytes = await antwort.Body?.transformToByteArray();
  if (!bytes) throw new Error('Die Datei konnte nicht geladen werden.');
  return new Blob([bytes as BlobPart], { type: antwort.ContentType ?? 'application/octet-stream' });
};

/** Löscht eine Datei — nur Admins dürfen das (Policy im Backend). */
export const dateiLoeschen = async (key: string): Promise<void> => {
  const { DeleteObjectCommand } = await import('@aws-sdk/client-s3');
  const client = await holeClient();
  await client.send(new DeleteObjectCommand({ Bucket: bucketName(), Key: key }));
};

export const dokumentSchluessel = (
  objektId: string,
  dokumentId: string,
  dateiname: string,
): string => {
  const sicherername = dateiname.replace(/[^\w.-]+/g, '_').slice(-120) || 'datei';
  return `objekte/${objektId}/${dokumentId}/${sicherername}`;
};
